// `type ComponentType` en NIET `Component` — zie de identieke toelichting in
// ParentShellV2. Deze regressie staat sinds mei 2026 meerdere keren in de
// auditreeks; het is dezelfde regel die telkens terugkomt.
import React, { useCallback, useState, Suspense, lazy, type ComponentType } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { getAdminRouteLabel, type AdminNavigationContext, type AdminRoute } from './adminNavigation';
// Lazy-loaded admin pages — each becomes its own bundle chunk so initial admin
// payload stays small and a single failing page can't crash the whole portal.
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({
  default: m.UsersPage
})));
const SchoolsPage = lazy(() => import('./pages/SchoolsPage').then(m => ({
  default: m.SchoolsPage
})));
const QuestionBankPage = lazy(() => import('./pages/QuestionBankPage').then(m => ({
  default: m.QuestionBankPage
})));
const GovernancePage = lazy(() => import('./pages/GovernancePage').then(m => ({
  default: m.GovernancePage
})));
const SupportContentPage = lazy(() => import('./pages/SupportContentPage').then(m => ({
  default: m.SupportContentPage
})));
const DiagnosticsPage = lazy(() => import('./pages/DiagnosticsPage').then(m => ({
  default: m.DiagnosticsPage
})));
const LogsPage = lazy(() => import('./pages/LogsPage').then(m => ({
  default: m.LogsPage
})));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({
  default: m.SettingsPage
})));
const MascotPage = lazy(() => import('./pages/MascotPage').then(m => ({
  default: m.MascotPage
})));
const LdgGraphPage = lazy(() => import('./pages/LdgGraphPage').then(m => ({
  default: m.LdgGraphPage
})));
const SessionAnalyticsPage = lazy(() => import('./pages/SessionAnalyticsPage').then(m => ({
  default: m.SessionAnalyticsPage
})));
const SsotRegistryPage = lazy(() => import('./pages/SsotRegistryPage').then(m => ({
  default: m.SsotRegistryPage
})));
const CodeRegistryPage = lazy(() => import('./pages/CodeRegistryPage').then(m => ({
  default: m.CodeRegistryPage
})));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage').then(m => ({
  default: m.DesignSystemPage
})));
import { type AccountsState, type Account } from '../../accounts';
import { ModuleId, Question } from '../../content';
import { AppState } from '../../app/types';
import { Menu, X, AlertCircle, Loader2 } from 'lucide-react';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
// VERWIJDERD: een hardcoded `ADMIN_PASSWORD` met een sessionStorage-vlag.
//
// Die poort deed niets. De controle stond clientside in de bundle, dus het
// wachtwoord was uit te lezen, en de ontgrendeling was één regel in de console
// (`sessionStorage.setItem('admin_unlocked','true')`). Erger dan nutteloos:
// een gedeeld wachtwoord maakt het auditspoor dat migratie 0008 met break glass
// opbouwt betekenisloos — bij één gedeeld geheim is niet te zien wíe er binnen
// was.
//
// De echte poort staat er al en zit op de juiste plek: `profiles.role = 'admin'`
// in de database, afgedwongen door RLS via `is_admin()`, en alleen handmatig te
// zetten. Wie geen admin is, krijgt hier hooguit lege schermen te zien — de
// data komt er niet doorheen.
export type { AdminRoute } from './adminNavigation';
/** Props passed to all admin page components via spread. */
type AdminPageProps = Omit<AdminShellProps, 'onNavigate'> & {
  navigationContext?: AdminNavigationContext | null;
  onNavigate: (route: AdminRoute, context?: AdminNavigationContext) => void;
};
const PAGE_MAP: Record<AdminRoute, ComponentType<Partial<AdminPageProps>>> = {
  dashboard: DashboardPage,
  users: UsersPage,
  schools: SchoolsPage,
  'question-bank': QuestionBankPage,
  governance: GovernancePage,
  ssot: SsotRegistryPage,
  'code-registry': CodeRegistryPage,
  'design-system': DesignSystemPage,
  'support-content': SupportContentPage,
  'ldg-graph': LdgGraphPage,
  mascot: MascotPage,
  diagnostics: DiagnosticsPage,
  logs: LogsPage,
  settings: SettingsPage,
  analytics: SessionAnalyticsPage
};
interface AdminShellProps {
  accounts?: AccountsState;
  getLearnerData?: (learnerId: string) => AppState | null;
  getLearnerModuleMastery?: (learnerId: string, moduleId: ModuleId) => number;
  selectedGroup?: number;
  onNavigate?: (view: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  allAccounts?: Account[];
  activeAccountId?: string;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
  questions?: Question[];
  state?: AppState;
}
export function AdminShell(props: AdminShellProps) {
  const [activeRoute, setActiveRoute] = useState<AdminRoute>('dashboard');
  const [navigationContext, setNavigationContext] = useState<AdminNavigationContext | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigateToRoute = useCallback((route: AdminRoute, context?: AdminNavigationContext) => {
    setNavigationContext(context ?? null);
    setActiveRoute(route);
    setMobileMenuOpen(false);
  }, []);
  const PageComponent = PAGE_MAP[activeRoute] ?? DashboardPage;
  return <div className="flex h-screen bg-gray-50 overflow-hidden" data-id="element-4389" data-mp="bn2wmkyp02pk">
      {/* Skip to content — a11y (A11Y-01 fix) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-4390" data-mp="6q2xyv46a28h">
        
        Ga naar inhoud
      </a>

      {/* Mobile Header with safe area */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-4391" data-mp="3o3g6rtee1zo0">
        
        <div className="font-bold text-gray-900 flex items-center gap-2.5 py-3" data-id="element-4392" data-mp="zcx7iwzgbh76">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-4393" data-mp="3s8ys7qpzzh29">
            <span className="text-white font-black text-sm" data-id="element-4394" data-mp="10kwuycsy10e">M</span>
          </div>
          <span className="text-sm" data-id="element-4395" data-mp="62jzg60pwtwa">{getAdminRouteLabel(activeRoute)}</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 -mr-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-4396" data-mp="a1sqv48acfoo">
          
          {mobileMenuOpen ? <X className="w-5 h-5" data-id="element-4397" data-mp="3ihkv1pliqtca" /> : <Menu className="w-5 h-5" data-id="element-4398" data-mp="3ih2mpull0oo0" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-gray-900/40 z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-4399" data-mp="3naneh4wl0mer" />
      

      {/* Sidebar - Desktop & Mobile Drawer */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-4400" data-mp="2n90h03sjkg3t">
        
        <AdminSidebar activeRoute={activeRoute} onNavigate={navigateToRoute} onLogout={props.onLogout} data-id="element-4401" data-mp="29c3rq6s2n6p1" />
        
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 min-h-0 overflow-hidden pt-14 md:pt-0" data-id="element-4402" data-mp="1ziwnwqczbyr2">
        
        <BaseErrorBoundary logPrefix="Admin Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-4404" data-mp="1ql47fiviul23">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4" data-id="element-4405" data-mp="1ljeyq9vrujh6">
                <AlertCircle className="w-8 h-8 text-indigo-600" data-id="element-4406" data-mp="1gn6jyxlhksaj" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-4407" data-mp="34ikv6dvudbuh">
                Er ging iets mis
              </h2>
              <p className="text-sm text-gray-500 mb-4 max-w-md" data-id="element-4408" data-mp="39eicamexim8b">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-4409" data-mp="30fxzg1w1v6s0">
              
                Probeer opnieuw
              </button>
            </div>} data-id="element-4403" data-mp="1ulo5qprtzupt">
          
          <Suspense fallback={<div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" role="status" aria-live="polite" data-id="element-4411" data-mp="2qk3a6nxbmdg9">
              
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin mb-3" data-id="element-4412" data-mp="2gt8r1sfdftz1" />
                <p className="text-sm text-gray-500" data-id="element-4413" data-mp="22rwbhi44hudo">
                  {getAdminRouteLabel(activeRoute)} laden…
                </p>
              </div>} data-id="element-4410" data-mp="2qjlb7jj9xl4n">
            
            <PageComponent {...props} navigationContext={navigationContext} onNavigate={navigateToRoute} data-id="element-4414" data-mp="27td9n5v832qk" />
            
          </Suspense>
        </BaseErrorBoundary>
      </main>
    </div>;
}